import type {
  CreateDeliveryPartnerInput,
  CreateDeliveryPartnerResponse,
  DeliveryPartnerFormOptions,
} from '@/types/delivery-partner';
import { useMutation, useQuery } from 'react-query';

import client from './client';
import { API_ENDPOINTS } from './client/api-endpoints';

/** Banks, regions, week days and periods are static server-side, so cache them for the session. */
export function useDeliveryPartnerFormOptions() {
  const { data, isLoading, error } = useQuery<DeliveryPartnerFormOptions, Error>(
    [API_ENDPOINTS.DELIVERY_PARTNERS_FORM_OPTIONS],
    () => client.deliveryPartners.formOptions(),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  return { options: data, isLoading, error };
}

interface DeliveryPartnerApiError {
  response?: {
    status?: number;
    data?: { message?: string | string[] };
  };
}

const FALLBACK_ERROR =
  'Não foi possível enviar sua candidatura. Tente novamente em instantes.';

export function resolveDeliveryPartnerError(error: unknown): string {
  const message = (error as DeliveryPartnerApiError)?.response?.data?.message;

  if (Array.isArray(message)) {
    return message[0] ?? FALLBACK_ERROR;
  }

  return message || FALLBACK_ERROR;
}

export function useCreateDeliveryPartner() {
  const { mutate, isLoading, isSuccess, data, error, reset } = useMutation<
    CreateDeliveryPartnerResponse,
    unknown,
    CreateDeliveryPartnerInput
  >((input) => client.deliveryPartners.create(input));

  return {
    createDeliveryPartner: mutate,
    isLoading,
    isSuccess,
    data,
    errorMessage: error ? resolveDeliveryPartnerError(error) : null,
    reset,
  };
}
